import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { setUploadGuidelineVideoDialogOpen } from '../../../redux/reducers/loginSlice';
import { Mail, MessageCircle, BookOpen, ExternalLink, ChevronDown, HelpCircle, ArrowRight, PlayCircle, FileText, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

const faqs = [
    {
        question: "How do I get AI feedback on my videos?",
        answer: "Simply upload your video in the 'Videos' section. Once uploaded, click on the 'Get AI Feedback' button. Our advanced biomechanic AI will analyze your movement and provide detailed reports within minutes."
    },
    {
        question: "Where can I find my performance reports?",
        answer: "All your generated reports are securely stored in the 'Reports' section of your dashboard. You can view, download, or share them from there at any time."
    },
    {
        question: "What video formats are supported?",
        answer: "We support most major video formats including MP4, MOV, and AVI. For the best AI analysis results, we recommend high-resolution (1080p) videos with stable lighting and a clear view of the athlete."
    },
    {
        question: "Is my data secure?",
        answer: "Yes, we take data privacy very seriously. All uploaded videos and generated reports are encrypted and stored on secure servers. Only you and authorized coaches have access to your data."
    }
];

const Support = () => {
    const [openFaq, setOpenFaq] = useState(null);
    const dispatch = useDispatch();

    const supportChannels = [
        {
            title: "Email Support",
            description: "Get in touch with our expert team for technical assistance.",
            icon: Mail,
            action: "support@visist.ai",
            link: "https://mail.google.com/mail/?view=cm&fs=1&to=support@visist.ai",
            isExternal: true
        },
        {
            title: "Community Help",
            description: "Join our active community of athletes and coaches on social media.",
            icon: MessageCircle,
            action: "Join Discussion",
            link: "https://www.linkedin.com/company/visist-ai/",
            isExternal: true
        },
        {
            title: "Documentation",
            description: "Access our detailed guides, case studies, and upload tutorials.",
            icon: BookOpen,
            isDocumentation: true
        }
    ];

    const docLinks = [
        {
            label: "Case Studies",
            icon: FileText,
            link: "/case-studies",
            type: "link"
        },
        {
            label: "Upload Guide Video",
            icon: PlayCircle,
            type: "action",
            action: () => dispatch(setUploadGuidelineVideoDialogOpen(true))
        }
    ];

    return (
        <div className="h-full bg-white text-slate-900 relative flex flex-col font-sans overflow-hidden">
            {/* Hero Section */}
            <div className="relative overflow-hidden bg-slate-50 border-b border-slate-100 py-16 px-4 md:px-8">
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-50 blur-3xl rounded-full translate-x-1/2 -translate-y-1/2 opacity-60" />
                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <h1 className="text-4xl md:text-5xl font-black text-slate-900 mb-6 tracking-tight uppercase">
                        Support <span className="text-[#247E45]">Center</span>
                    </h1>
                    <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
                        Need help with your profile or performance analysis? Our dedicated support team is ready to assist you.
                    </p>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-12 md:px-8 lg:px-12 bg-white selection:bg-emerald-100">
                <div className="max-w-6xl mx-auto space-y-16 pb-20">

                    {/* Support Channels Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {supportChannels.map((channel, idx) => {
                            if (channel.isDocumentation) {
                                return (
                                    <div
                                        key={idx}
                                        className="relative p-8 rounded-3xl bg-white border border-slate-100 shadow-sm transition-all duration-300 hover:shadow-xl hover:shadow-emerald-900/5 hover:-translate-y-1 h-full flex flex-col"
                                    >
                                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 bg-emerald-50 text-[#247E45]">
                                            <channel.icon size={28} strokeWidth={1.5} />
                                        </div>
                                        <h3 className="text-xl font-bold text-slate-900 mb-2">{channel.title}</h3>
                                        <p className="text-slate-500 text-sm leading-relaxed mb-6">
                                            {channel.description}
                                        </p>

                                        <div className="mt-auto space-y-3">
                                            {docLinks.map((doc, dIdx) => (
                                                doc.type === "link" ? (
                                                    <Link
                                                        key={dIdx}
                                                        to={doc.link}
                                                        className="flex items-center gap-2 text-sm font-bold text-slate-700 hover:text-[#247E45] transition-all group/link"
                                                    >
                                                        <doc.icon size={16} className="text-[#247E45] transition-colors" />
                                                        {doc.label} <ArrowRight size={14} className="ml-auto text-slate-300 group-hover/link:text-[#247E45] group-hover/link:translate-x-1 transition-all" />
                                                    </Link>
                                                ) : (
                                                    <button
                                                        key={dIdx}
                                                        onClick={doc.action}
                                                        className="flex items-center gap-2 text-sm font-bold text-slate-700 hover:text-[#247E45] transition-all group/link w-full text-left"
                                                    >
                                                        <doc.icon size={16} className="text-[#247E45] transition-colors" />
                                                        {doc.label} <ArrowRight size={14} className="ml-auto text-slate-300 group-hover/link:text-[#247E45] group-hover/link:translate-x-1 transition-all" />
                                                    </button>
                                                )
                                            ))}
                                        </div>
                                    </div>
                                );
                            }

                            const Content = (
                                <div className="group relative p-8 rounded-3xl bg-white border border-slate-100 shadow-sm transition-all duration-300 hover:shadow-xl hover:shadow-emerald-900/5 hover:-translate-y-1 h-full">
                                    <div className="absolute top-0 right-0 p-6 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <ExternalLink size={18} className="text-slate-300" />
                                    </div>
                                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110 duration-300 bg-emerald-50 text-[#247E45]`}>
                                        <channel.icon size={28} strokeWidth={1.5} />
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-2">{channel.title}</h3>
                                    <p className="text-slate-500 text-sm leading-relaxed mb-6">
                                        {channel.description}
                                    </p>
                                    <span className="inline-flex items-center text-sm font-bold text-[#247E45] group-hover:gap-2 transition-all">
                                        {channel.action} <ArrowRight size={16} className="ml-1" />
                                    </span>
                                </div>
                            );

                            return (
                                <a key={idx} href={channel.link} target="_blank" rel="noopener noreferrer">
                                    {Content}
                                </a>
                            );
                        })}
                    </div>

                    {/* FAQ Section */}
                    <div className="max-w-4xl mx-auto">
                        <div className="flex items-center justify-center gap-3 mb-10">
                            <div className="w-10 h-10 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center">
                                <HelpCircle size={24} />
                            </div>
                            <h2 className="text-2xl font-bold text-slate-900 uppercase tracking-wider">Frequently Asked Questions</h2>
                        </div>

                        <div className="space-y-4">
                            {faqs.map((faq, idx) => {
                                const isOpen = openFaq === idx;
                                return (
                                    <div
                                        key={idx}
                                        className={`rounded-2xl border transition-all duration-300 ${isOpen ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-100 bg-white hover:border-slate-200 shadow-sm'}`}
                                    >
                                        <button
                                            onClick={() => setOpenFaq(isOpen ? null : idx)}
                                            className="w-full px-8 py-5 flex items-center justify-between text-left"
                                        >
                                            <span className={`font-bold transition-colors ${isOpen ? 'text-[#247E45]' : 'text-slate-800'}`}>
                                                {faq.question}
                                            </span>
                                            <ChevronDown
                                                size={20}
                                                className={`transition-transform duration-300 text-slate-400 ${isOpen ? 'rotate-180 text-[#247E45]' : ''}`}
                                            />
                                        </button>
                                        <div className={`overflow-hidden transition-all duration-300 max-h-0 ${isOpen ? 'max-h-40 py-5' : ''}`}>
                                            <p className="px-8 text-slate-600 text-sm leading-relaxed">
                                                {faq.answer}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Footer Contact */}
                    <div className="text-center p-12 rounded-[3rem] bg-slate-900 text-white relative overflow-hidden">
                        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-sky-500" />
                        <h4 className="text-2xl font-bold mb-4">Still have questions?</h4>
                        <p className="text-slate-400 mb-8 max-w-lg mx-auto">
                            Our team is here to help you get the most out of your training.
                        </p>
                        <a
                            href="https://mail.google.com/mail/?view=cm&fs=1&to=support@visist.ai"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-8 py-4 bg-[#247E45] hover:bg-[#1f6b3a] text-white font-bold rounded-2xl transition-all shadow-xl shadow-emerald-900/20 active:scale-95"
                        >
                            Email Our Team
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Support;
